import { formatFields } from "../utils/formatFormField";
import {
  getSDKAllowedPaymentEventData,
  getSDKAllowedPayoutEventData,
} from "../utils/getSDKAllowedEventData";
import { mapSavedCards } from "../../utils/mapSavedCards";

export const formatPayoutResponse = (response: PayoutApiResponse) => {
  const {
    intent,
    action_data: actionData,
    selected_method: selectedMethodSlug,
    intent_sdk_state: intentSdkState,
  } = response.data;

  return getSDKAllowedPayoutEventData({
    intent,
    actionData,
    selectedMethodSlug,
    intentSdkState,
  });
};

export const formatPaymentResponse = (response: {
  data: PaymentDataResponse;
}) => {
  const {
    intent,
    action_data: actionData,
    selected_method: selectedMethodSlug,
    intent_sdk_state: intentSdkState,
    next_action: nextAction,
    partial_wallet: partialWallet,
    last_used_method: lastUsedMethod,
    plan_details: planDetails,
    subscription,
  } = response.data;

  return getSDKAllowedPaymentEventData({
    intent,
    actionData,
    selectedMethodSlug,
    intentSdkState,
    nextAction,
    partialWallet,
    lastUsedMethod,
    planDetails,
    subscription,
  });
};

export const formatMethodsForPaymentIntent = (response: PaymentApiResponse) => {
  const {
    intent,
    saved_cards: savedCards,
    selected_method: selectedMethodSlug,
    saved_bankaccounts: savedBankAccounts,
    wallet,
  } = response.data;

  const paymentMethods = intent.payment_methods.filter(
    (method) => !method.use_for_express_checkout
  );
  const expressMethods = intent.payment_methods.filter(
    (method) => method.use_for_express_checkout
  );

  return formatPaymentMethods({
    paymentMethods,
    expressMethods,
    savedCards,
    savedBankAccounts,
    wallet,
    selectedMethodSlug,
  });
};

export const formatPaymentMethods = ({
  paymentMethods = [],
  expressMethods = [],
  savedCards = [],
  savedBankAccounts = [],
  wallet = 0,
  selectedMethodSlug,
}: {
  paymentMethods: PaymentMethod[];
  expressMethods: PaymentMethod[];
  savedCards?: SavedCard[];
  savedBankAccounts?: SavedBankAccount[];
  wallet?: number;
  selectedMethodSlug?: PaymentMethodSlugs;
}) => {
  return {
    paymentMethods: paymentMethods.map((method) => ({
      id: method.payment_method,
      title: method.payment_method_name,
      icons: method.checkout_icons,
      isSelected: selectedMethodSlug === method.payment_method,
      confirmationRequired: method.confirmation_required,
      requiredBillingFields:
        method.required_billing_fields?.length > 0
          ? formatFields({
              fields: method.required_billing_fields,
              providerName: method.provider_name,
              providerExtraData: method.provider_extra_data,
              providerApiName: method.provider_api_name,
            })
          : null,
      requiredShippingFields:
        method.required_shipping_fields?.length > 0
          ? formatFields({
              fields: method.required_shipping_fields,
              providerName: method.provider_name,
              providerExtraData: method.provider_extra_data,
              providerApiName: method.provider_api_name,
            })
          : null,
      nativePayData: null,
    })),
    expressMethods: expressMethods.map((method) => ({
      id: method.payment_method,
      title: method.payment_method_name,
      icons: method.checkout_icons,
      isSelected: selectedMethodSlug === method.payment_method,
      confirmationRequired: method.confirmation_required,
      requiredBillingFields:
        method.required_billing_fields?.length > 0
          ? formatFields({
              fields: method.required_billing_fields,
              providerName: method.provider_name,
              providerExtraData: method.provider_extra_data,
              providerApiName: method.provider_api_name,
            })
          : null,
      requiredShippingFields:
        method.required_shipping_fields?.length > 0
          ? formatFields({
              fields: method.required_shipping_fields,
              providerName: method.provider_name,
              providerExtraData: method.provider_extra_data,
              providerApiName: method.provider_api_name,
            })
          : null,
      nativePayData: method.native_pay_data,
    })),
    savedCards: mapSavedCards(savedCards).map((card) => ({
      id: card.id,
      brand: card.brand,
      logo: card.logo,
      first6Digits: card.bin,
      last4: card.last_4,
      expiryMonth: card.expiry_month,
      expiryYear: card.expiry_year,
      country: card.country,
      requiresCvv: card.requires_cvv,
    })),
    savedBankAccounts: savedBankAccounts.map((account) => ({
      id: account.id,
      bankIdentifier: account.bank_identifier,
      status: account.status,
      accounts: account.accounts.map((acc) => ({
        accountName: acc.account_name,
      })),
      logo: account.logo,
    })),
    customerBalances: [
      {
        id: "SELFSERVE_WALLET",
        balance: wallet,
        icon: "https://embed.moneyhash.io/assets/selfWalletIcon.767086fb.svg",
        isSelected: selectedMethodSlug === "SELFSERVE_WALLET",
      },
    ],
  };
};

export const formatRequiredFields = (requiredBillingFields: FormField[]) => {
  return requiredBillingFields.map(({ error_messages: _error, ...rest }) => ({
    ...rest,
  }));
};

export const formatMethodsForPayoutIntent = (response: PayoutApiResponse) => {
  const { intent, selected_method: selectedMethodSlug } = response.data;

  return {
    payoutMethods: intent.payout_methods.map((method) => ({
      id: method.payout_method,
      title: method.payout_method_name,
      icons: method.checkout_icons,
      isSelected: selectedMethodSlug === method.payout_method,
      confirmationRequired: method.confirmation_required,
    })),
  };
};

export const formatCardTokenResponse = (response: CardApiResponse) => {
  const { action_data, intent_sdk_state, next_action } = response.data;
  return {
    state: intent_sdk_state,
    stateDetails:
      intent_sdk_state === "URL_TO_RENDER"
        ? {
            url: action_data.redirect.redirect_url,
            redirectStrategy: next_action,
          }
        : null,
  };
};
